import React, { FC, useEffect } from 'react';
import { connect } from 'react-redux';
import { Row, Col } from 'antd/lib/grid';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import './style.scss';
import { Props } from './typing';
import InputField from '../../../../../../components/TextInput';
import ProfileFooter from '../ProfileFooter';
import { urlValidationRgex } from 'lib/constants';

const MandatoryDetails: FC<Props> = (props): JSX.Element => {
  const {
    nextPage,
    prevPage,
    currentStep,
    setCurrentStep,
    merchantProfile,
    updateProfileDetails,
    validationErrors,
  } = props;

  const formik = useFormik({
    initialValues: {
      Privacy_Policy: merchantProfile?.mandatorydetails?.Privacy_Policy || '',
      Cancellation_Refund_Policy:
        merchantProfile?.mandatorydetails?.Cancellation_Refund_Policy || '',
      TermsnConditions:
        merchantProfile?.mandatorydetails?.TermsnConditions || '',
      ProductServiceDesc:
        merchantProfile?.mandatorydetails?.ProductServiceDesc || '',
      PriceCostInfo: merchantProfile?.mandatorydetails?.PriceCostInfo || '',
      RelationbnWebsite_LegalName:
        merchantProfile?.mandatorydetails?.RelationbnWebsite_LegalName || '',
      HelpDesk: merchantProfile?.mandatorydetails?.HelpDesk || '',
    },
    validationSchema: Yup.object({
      Privacy_Policy: Yup.string()
        .trim()
        .matches(urlValidationRgex, validationErrors.INVALID_URL),
      Cancellation_Refund_Policy: Yup.string()
        .trim()
        .matches(urlValidationRgex, validationErrors.INVALID_URL),
      TermsnConditions: Yup.string()
        .trim()
        .matches(urlValidationRgex, validationErrors.INVALID_URL),
      ProductServiceDesc: Yup.string()
        .trim()
        .matches(urlValidationRgex, validationErrors.INVALID_URL),
      PriceCostInfo: Yup.string()
        .trim()
        .matches(urlValidationRgex, validationErrors.INVALID_URL),
      RelationbnWebsite_LegalName: Yup.string()
        .trim()
        .matches(urlValidationRgex, validationErrors.INVALID_URL),
      HelpDesk: Yup.string().trim()
        .matches(urlValidationRgex, validationErrors.INVALID_PHONE),
    }),
    onSubmit: () => {},
  });

  useEffect(() => {
    if (merchantProfile?.mandatorydetails) {
      formik.setValues({
        ...formik.values,
        Privacy_Policy: merchantProfile?.mandatorydetails?.Privacy_Policy || '',
        Cancellation_Refund_Policy:
          merchantProfile?.mandatorydetails?.Cancellation_Refund_Policy || '',
        TermsnConditions:
          merchantProfile?.mandatorydetails?.TermsnConditions || '',
        ProductServiceDesc:
          merchantProfile?.mandatorydetails?.ProductServiceDesc || '',
        PriceCostInfo: merchantProfile?.mandatorydetails?.PriceCostInfo || '',
        RelationbnWebsite_LegalName:
          merchantProfile?.mandatorydetails?.RelationbnWebsite_LegalName || '',
        HelpDesk: merchantProfile?.mandatorydetails?.HelpDesk || '',
      });
    }
  }, [merchantProfile?.mandatorydetails]);

  const callFormSubmit = (step, statusData) => {
    const merchantProfileValue = {
      ...merchantProfile,
      mandatorydetails: {
        ...formik.values,
      },
    };
    updateProfileDetails(step, statusData, merchantProfileValue);
  };

  const enableSave = () => {
    return (
      Object.keys(formik.values).filter((key) => {
        return formik.values[key] !== '';
      }).length > 0
    );
  };

  const enableSaveAndNext = () => {
    return (
      Object.keys(formik.values).filter((key) => {
        return formik.values[key] !== '';
      }).length === Object.keys(formik.values).length &&
      Object.keys(formik.errors).length === 0
    );
  };

  return (
    <form onSubmit={formik.handleSubmit}>
      <div className='kyc-mandatory-details'>
        <div className='title'>Mandatory Details</div>
        <Row>
          <Col span={10}>
            <div className='content-text'>
            </div>
          </Col>
        </Row>
        <div className='plural-mandatory-details'>
          <Row>
            <Col span={6}>
              <InputField
                name='Privacy_Policy'
                type='url'
                label='Privacy Policy URL'
                isRequired={true}
                value={formik.values.Privacy_Policy}
                placeholder='https://example.com'
                className='form-control'
                onBlur={formik.handleBlur}
                handleChange={formik.handleChange}
                readonly={false}
                error={Boolean(
                  formik.touched.Privacy_Policy && formik.errors.Privacy_Policy
                )}
                errorMessage={formik.errors.Privacy_Policy?.toString()}
              />
            </Col>
            <Col span={6} offset={1}>
              <InputField
                name='Cancellation_Refund_Policy'
                type='url'
                label='Cancellation/Refund Policy URL'
                isRequired={true}
                value={formik.values.Cancellation_Refund_Policy}
                placeholder='https://example.com'
                className='form-control'
                onBlur={formik.handleBlur}
                handleChange={formik.handleChange}
                readonly={false}
                error={Boolean(
                  formik.touched.Cancellation_Refund_Policy &&
                    formik.errors.Cancellation_Refund_Policy
                )}
                errorMessage={formik.errors.Cancellation_Refund_Policy?.toString()}
              />
            </Col>
          </Row>

          <Row>
            <Col span={6}>
              <InputField
                name='TermsnConditions'
                type='url'
                label='Terms and Conditions URL'
                isRequired={true}
                value={formik.values.TermsnConditions}
                placeholder='https://example.com'
                className='form-control'
                onBlur={formik.handleBlur}
                handleChange={formik.handleChange}
                readonly={false}
                error={Boolean(
                  formik.touched.TermsnConditions &&
                    formik.errors.TermsnConditions
                )}
                errorMessage={formik.errors.TermsnConditions?.toString()}
              />
            </Col>
            <Col span={6} offset={1}>
              <InputField
                name='ProductServiceDesc'
                type='url'
                label='Product/Service Description'
                isRequired={true}
                value={formik.values.ProductServiceDesc}
                placeholder='https://example.com'
                className='form-control'
                onBlur={formik.handleBlur}
                handleChange={formik.handleChange}
                readonly={false}
                error={Boolean(
                  formik.touched.ProductServiceDesc &&
                    formik.errors.ProductServiceDesc
                )}
                errorMessage={formik.errors.ProductServiceDesc?.toString()}
              />
            </Col>
          </Row>

          <Row>
            <Col span={6}>
              <InputField
                name='PriceCostInfo'
                type='text'
                label='Price Information'
                isRequired={true}
                value={formik.values.PriceCostInfo}
                placeholder='https://example.com'
                className='form-control'
                onBlur={formik.handleBlur}
                handleChange={formik.handleChange}
                readonly={false}
                error={Boolean(
                  formik.touched.PriceCostInfo && formik.errors.PriceCostInfo
                )}
                errorMessage={formik.errors.PriceCostInfo?.toString()}
              />
            </Col>
            <Col span={6} offset={1}>
              <InputField
                name='RelationbnWebsite_LegalName'
                type='text'
                label='Relationship between website and legal name'
                isRequired={true}
                value={formik.values.RelationbnWebsite_LegalName}
                placeholder='https://example.com'
                className='form-control'
                onBlur={formik.handleBlur}
                handleChange={formik.handleChange}
                readonly={false}
                error={Boolean(
                  formik.touched.RelationbnWebsite_LegalName &&
                    formik.errors.RelationbnWebsite_LegalName
                )}
                errorMessage={formik.errors.RelationbnWebsite_LegalName?.toString()}
              />
            </Col>
          </Row>

          <Row>
            <Col span={6}>
            <InputField
                name='HelpDesk'
                type='text'
                label='Help Desk Contact Information'
                isRequired={true}
                value={formik.values.HelpDesk}
                placeholder='https://example.com'
                className='form-control'
                onBlur={formik.handleBlur}
                handleChange={formik.handleChange}
                readonly={false}
                error={Boolean(
                  formik.touched.HelpDesk &&
                    formik.errors.HelpDesk
                )}
                errorMessage={formik.errors.HelpDesk?.toString()}
              />
            </Col>
          </Row>
          <div className='divider' />
        </div>
      </div>
      <ProfileFooter
        nextPage={nextPage}
        prevPage={prevPage}
        currentStep={currentStep}
        setCurrentStep={setCurrentStep}
        saveAction={callFormSubmit}
        enableSave={enableSave()}
        enableSaveAndNext={enableSaveAndNext()}
      />
    </form>
  );
};

const mapStateToProps = ({ validationReducer }) => ({
  validationErrors: validationReducer.validationErrorState.validationErrors,
});

export default connect(mapStateToProps, {})(MandatoryDetails);
